import React from "react";
import { formatMoney } from "../services/helpers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { far } from "@fortawesome/pro-regular-svg-icons";
import PropTypes from "prop-types";
import { injectIntl, defineMessages } from "react-intl";
import { Card, Col, Row, Badge } from "react-bootstrap";
import StatusBadge from "./StatusBadge";
import PaydateText from "./PaydateText";
import useI18n from "../hooks/useI18n";
import get from "lodash.get";
import useEnv from "../hooks/useEnv";

// Strings for translation in this view
const messages = defineMessages({
    creditor: {
        id: 'Laskuttaja',
        defaultMessage: 'Laskuttaja'
    },
    dueDate: {
        id: 'Eräpäivä',
        defaultMessage: 'Eräpäivä'
    },
    openTotal: {
        id: 'Avoinna',
        defaultMessage: 'Avoinna'
    },
    paidTotal: {
        id: 'Maksettu',
        defaultMessage: 'Maksettu'
    },
    status: {
        id: 'Tila',
        defaultMessage: 'Tila'
    },
    signServing: {
        id: "Allekirjoita tiedoksianto",
        defaultMessage: "Allekirjoita tiedoksianto"
    },
    openInvoiceLabel: {
        id: "Avaa laskusivu",
        defaultMessage: "Avaa laskusivu"
    }
});
export { messages };

const InvoiceCard = (props) => {

    // Translations function
    const lng = props.intl.formatMessage;
    const { getEnv, isEnvTruthy } = useEnv();
    const { getLocale } = useI18n();
    const userLocale = getLocale();
    const allowServing = isEnvTruthy('SHOW_SERVING');

    const creditor = props.job.payee?.name;
    const openTotal = props.job.openamount;
    const paidTotal = props.job.paidamount;
    const statusCode = props.job.job_main_statuscode;
    const jobStatus = props.job.jobstatus;
    const dueDate = props.job.paydate;
    const signatureNeeded = (allowServing && props.job.serving_data?.data?.serving_type === 'letter' && props.job.serving_data?.data?.serving_need_signature);

    /**
     * Get currency from job object
     * If job doesn't have currency, use default currency
     * @param job
     * @return {string} currency
     */
    const getCurrencyFromJob = (job) => {
        // Get currency from job data
        let currency = get(job, 'currency', false);
        // If currency was not found, use the default currency
        currency = currency && currency !== '' ? currency : getEnv('DEFAULT_CURRENCY');
        return currency;
    }

    const currency = getCurrencyFromJob(props.job);

    return (
        <Card className={"invoice invoice-card" + (signatureNeeded ? " warning" : "")} aria-label={lng(messages.openInvoiceLabel)}>
            <Card.Body>
                <Row>
                    <Col xs={6} lg={3} className="mb-2 mb-md-0">
                        <p className="text-muted fs-6 mb-0">{lng(messages.creditor)}</p>
                        <h5 className="text-break">{creditor}</h5>
                    </Col>
                    <Col xs={6} lg={3}>
                        <p className="text-muted fs-6 mb-0">{lng(messages.dueDate)}</p>
                        <h5>
                            <PaydateText
                                intl={props.intl}
                                statuscode={jobStatus ?? null}
                                paydate={dueDate ?? null}
                            />
                        </h5>
                    </Col>
                    <Col xs={6} lg={3}>
                        <p className="text-muted fs-6 mb-0">
                            {openTotal > 0 ? lng(messages.openTotal) : ''} 
                            {openTotal > 0 && paidTotal > 0 ? ' / ': ''} 
                            {paidTotal > 0 ? lng(messages.paidTotal) : ''}
                        </p>
                        <h5>
                            {openTotal > 0 ? formatMoney(openTotal, userLocale, currency) : ''}
                            {openTotal > 0 && paidTotal > 0 ? ' / ': ''} 
                            {paidTotal > 0 ? formatMoney(paidTotal, userLocale, currency) : ''}
                        </h5>
                    </Col>
                    <Col xs={6} lg={3}>
                        <p className="text-muted fs-6 mb-0">{lng(messages.status)}</p>
                        {signatureNeeded ? (
                            <Badge bg="warning" className="text-wrap">{lng(messages.signServing)}</Badge>
                        ) : (
                            <StatusBadge statusCode={statusCode} dueDate={dueDate} lng={lng} />
                        )}
                    </Col>
                </Row>
            </Card.Body>
            <div className={"invoice-card-icon" + (signatureNeeded ? " warning" : "")}>
                <FontAwesomeIcon icon={far.faChevronRight} aria-hidden="true" />
            </div>
        </Card>
    );
};

InvoiceCard.propTypes = {
    'intl': PropTypes.object.isRequired,
    'job': PropTypes.object.isRequired
};

export default injectIntl(InvoiceCard);
